import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, MessageSquare } from "lucide-react";

const CANNED = ["I'm on my way", "Thank you so much!", "Running 10 minutes late", "All done, take care"];

export default function Messages() {
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [live, setLive] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get("/conversations").then((r) => setConvos(r.data)); }, []);

  const activeRequestId = active?.request_id;
  useEffect(() => {
    if (!activeRequestId) return;
    const load = () => api.get(`/messages/${activeRequestId}`).then((r) => setMessages(r.data));
    load();
    const token = localStorage.getItem("kindred_token");
    const wsUrl = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws/messages/${activeRequestId}?token=${token}`;
    let ws;
    let fallback;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (ev) => {
        const m = JSON.parse(ev.data);
        setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
      };
      ws.onopen = () => setLive(true);
      ws.onclose = () => { setLive(false); fallback = setInterval(load, 5000); };
    } catch {
      fallback = setInterval(load, 5000);
    }
    return () => { ws?.close(); if (fallback) clearInterval(fallback); };
  }, [activeRequestId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (body) => {
    const t = (body ?? text).trim();
    if (!t) return;
    setText("");
    const { data } = await api.post("/messages", { request_id: active.request_id, text: t });
    setMessages((prev) => (prev.some((x) => x.id === data.id) ? prev : [...prev, data]));
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
      <h1 className="text-3xl font-display font-bold text-slate-900 mb-6">Messages</h1>
      <div className="grid md:grid-cols-3 gap-5 h-[calc(100vh-220px)] min-h-[480px]">
        <div className="bg-white border border-[#E2D9CE] rounded-2xl overflow-y-auto">
          {convos.length === 0 ? (
            <div className="p-6 text-slate-600">
              <MessageSquare className="w-8 h-8 text-slate-300 mb-2" />
              No conversations yet. They appear once a request is matched.
            </div>
          ) : convos.map((c) => (
            <button key={c.request_id} data-testid={`convo-${c.request_id}`} onClick={() => setActive(c)}
              className={`w-full text-left px-4 py-4 border-b border-[#E2D9CE] hover:bg-[#FAF8F5] transition-colors ${active?.request_id === c.request_id ? "bg-[#FBEBE4]" : ""}`}>
              <p className="font-semibold text-slate-900">{c.title}</p>
              <p className="text-sm text-slate-500">with {c.other_name}</p>
              {c.last_message && <p className="text-sm text-slate-400 truncate mt-1">{c.last_message}</p>}
            </button>
          ))}
        </div>

        <div className="md:col-span-2 bg-white border border-[#E2D9CE] rounded-2xl flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-lg">Select a conversation</div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-[#E2D9CE] flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{active.title}</p>
                  <p className="text-sm text-slate-500">with {active.other_name}</p>
                </div>
                <span data-testid="live-indicator" className={`flex items-center gap-1.5 text-sm font-medium ${live ? "text-[#2E7D6B]" : "text-slate-400"}`}>
                  <span className={`w-2.5 h-2.5 rounded-full ${live ? "bg-[#2E7D6B] animate-pulse" : "bg-slate-300"}`} /> {live ? "Live" : "Connecting…"}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_id === user.id ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${m.sender_id === user.id ? "bg-[#D96B43] text-white rounded-br-sm" : "bg-[#F2ECE4] text-slate-800 rounded-bl-sm"}`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="px-4 pt-2 flex flex-wrap gap-2">
                {CANNED.map((c) => (
                  <button key={c} onClick={() => send(c)} className="text-sm bg-[#E8F4F1] text-[#2E7D6B] px-3 py-1.5 rounded-full font-medium hover:bg-[#2E7D6B] hover:text-white transition-colors">{c}</button>
                ))}
              </div>
              <div className="p-3 flex gap-2">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Type a message…" className="flex-1 px-4 min-h-[48px] rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] outline-none" />
                <button data-testid="send-message-btn" onClick={() => send()} className="bg-[#D96B43] hover:bg-[#C25832] text-white rounded-xl px-4 min-h-[48px] flex items-center transition-colors">
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
